import { isObject } from 'Utils';
import { IFormModelData } from './types';

export class BaseFormModelDataHandler {
  data: IFormModelData;

  constructor(data: IFormModelData) {
    this.data = data;
  }

  /**
   * Get all fields data
   * @param fields - The fields object that defines the structure of the data. Defaults to the result of calling the getFields() method.
   * @param data - The data object from which to extract field data. Defaults to the current instance's data property.
   */
  getDataByFields(fields: IFormModelData, data = this.data) {
    const fieldsData: IFormModelData = {};

    // Iterate over each key in the fields object
    Object.keys(fields)
      .forEach((key) => {
        const value = data[key];
        const fieldsValue = fields[key];

        // If both the value in the data object and the corresponding field in the fields object are objects,
        // recursively call getFieldsData() to extract nested field data
        if (isObject(value) && isObject(fieldsValue)) {
          fieldsData[key] = this.getDataByFields(fields[key] as IFormModelData, value as IFormModelData);
        } else {
          // If the value is not an object, assign it directly to the fieldsData object
          fieldsData[key] = value;
        }
      });

    return fieldsData;
  }

  /**
   * Get changed data from form data
   * @param model - model data which is getting from server
   * @param defaultData - fields with default data which is getting from getFields() method
   * @param data - form data which is changing in form inputs
   */
  getChangedData(
    model: IFormModelData,
    defaultData: IFormModelData,
    data: IFormModelData = this.data,
  ): IFormModelData {
    const changedData: IFormModelData = {};

    // iterate over data keys - fields of form
    Object.keys(defaultData)
      .forEach((key) => {
        const dataValue = data[key];
        const modelValue = model?.[key];
        const defaultValue = defaultData[key];
        // if data value is object and model or default value is object
        if (isObject(dataValue) && (isObject(modelValue) || isObject(defaultValue))) {
          // get changed data from sub object
          const subChangedData = this.getChangedData(
            (modelValue || {}) as IFormModelData,
            defaultValue as IFormModelData,
            dataValue as IFormModelData,
          );
          // if sub object has changed data - add it to changed data
          if (Object.keys(subChangedData).length) {
            changedData[key] = subChangedData;
          }
          // otherwise if such key is in model and data value is not equal to model value
          // or data value is not equal to default value
        } else if ((key in model && dataValue !== modelValue) || dataValue !== defaultValue) {
          // add this value to changed data
          changedData[key] = dataValue;
        }
      });

    return changedData;
  }

  setData(data: IFormModelData) {
    Object.assign(this.data, data);
  }
}
